import React, {useEffect, useRef, useState} from 'react';
import 'mapbox-gl/dist/mapbox-gl.css';
import mapboxgl from 'mapbox-gl'; // eslint-disable-line import/no-webpack-loader-syntax
import Map, {Marker, Popup, Source, Layer} from 'react-map-gl';
import geoViewport from '@mapbox/geo-viewport';
import {ReactComponent as MarkerElement} from './location-pin.svg';

import styles from './styles.module.css';

mapboxgl.accessToken = 'pk.eyJ1IjoibHBlbmF1ZCIsImEiOiJja3IyOWxxcDkyOWR3MzFxcDl6NDVyeHEwIn0.rTy3hffJaDGKECqiY3OhqQ';

const popupOffsets = {
  left: [20, -30],
  right: [-24, -30],
  top: [0, 0],
  bottom: [0, -60],
  center: [0, -10],
  'top-left': [20, -30],
  'top-right': [-24, -30],
  'bottom-left': [20, -30],
  'bottom-right': [-24, -30]
};

export default React.forwardRef(function SucrineMap({config, points, path, onClick, mapOptions = {}}, map) {

  // const localRef = useRef(null);
  const localMapRef = useRef(map);

  const [hiddenPopups, setHiddenPopups] = useState({});

  useEffect(() => {
    if (points) {
      const hp = {};
      points.forEach((point, pointIndex) => {
        if (point.hideablePopup && point.hiddenPopup) {
          hp[`key_${pointIndex}`] = true;
        }
      });
      setHiddenPopups(hp);
    }
  }, [points]);

  function checkPointsViewport() {
    if (!points || !localMapRef.current?.getMap()) {
      return;
    }
    // Compute max points to see if any point is out of viewport
    const bounds = localMapRef.current.getMap().getBounds();
    let oneOutside = false;
    points.forEach((point) => {
      const [lng, lat] = point.coordinates;
      if (!(lat >= bounds._sw.lat && lat <= bounds._ne.lat && lng >= bounds._sw.lng && lng <= bounds._ne.lng)) {
        oneOutside = true;
      }
    });
    if (oneOutside) {
      // Compute new rect
      const bounds = [Infinity, Infinity, -1 * Infinity, -1 * Infinity];
      points.forEach((point) => {
        if (isValidCoordinates(point.coordinates)) {
          const [lng, lat] = point.coordinates;
          if (lng < bounds[0]) {
            bounds[0] = lng;
          }
          if (lat < bounds[1]) {
            bounds[1] = lat;
          }
          if (lng > bounds[2]) {
            bounds[2] = lng;
          }
          if (lat > bounds[3]) {
            bounds[3] = lat;
          }
        }
      });
      const viewport = geoViewport.viewport(bounds, [window.innerWidth, window.innerHeight]);
      localMapRef.current.getMap().setCenter(viewport.center);
      localMapRef.current.getMap().setZoom(viewport.zoom * 0.9);
    }
  }

  function handleMapLoad() {
    if (localMapRef.current && config) {
      try {
        localMapRef.current.getMap().setCenter(config.center);
        localMapRef.current.getMap().setZoom(config.zoomLevel);
        if (points) {
          checkPointsViewport();
        }
      } catch(err) {
        console.error(err);
      }
    }
  }

  function getPath() {
    if (path) {
      try {
        const source = {
          type: 'geojson',
          data: {
            type: 'FeatureCollection',
            features: [
              {
                type: 'Feature',
                properties: {},
                geometry: path.routes[0].geometry
              }
            ]
          }
        };

        const layer = {
          id: 'direction_layer',
          type: 'line',
          // source: 'direction_data',
          layout: {
            'line-join': 'round',
            'line-cap': 'round'
          },
          paint: {
            'line-color': '#637e76',
            // 'line-gap-width': 3,
            'line-dasharray': [2, 2],
            'line-width': 3,
            'line-opacity': 0.6
          }
        }

        return (<Source id="direction_data" {...source}>
          <Layer {...layer} />
        </Source>);
      } catch(err) {
        console.error(err)
      }
      return null;
    }
  }

  function handleMarkerClick(pointIndex) {
    return () => {
      if (points[pointIndex].hideablePopup === true) {
        const hp = {...hiddenPopups};
        if (hp[`key_${pointIndex}`] === undefined) {
          hp[`key_${pointIndex}`] = true;
        } else {
          hp[`key_${pointIndex}`] = !hp[`key_${pointIndex}`];
        }
        setHiddenPopups(hp);
      }
    }
  }

  function isValidCoordinates(coords) {
    if (!Array.isArray(coords) || coords.length !== 2) {
      return false;
    }
    if (!Number.isFinite(coords[0])
      || !Number.isFinite(coords[1])
      || coords[0] < -180
      || coords[0] > 180
      || coords[1] < -90
      || coords[1] > 90) {
      return false;
    }
    return true;
  }

  function getMapPoints() {
    if (points) {
      try {
        return points.map((mapPoint, pointIndex) => {
          const color = mapPoint.type === 'site' ? '#205143' : '#D46330';
          const key = `key_${pointIndex}`;
          if (!isValidCoordinates(mapPoint?.coordinates)) {
            return null;
          }
          if (mapPoint.label) {
            // const popupAnchor = mapPoint.type === 'poi' ? offset : (mapPoint.popupAnchor || 'left');
            const popupAnchor = mapPoint.popupAnchor || 'left';
            return (
              <React.Fragment key={key}>
                {mapPoint.hideablePopup === true && hiddenPopups[key] ? null : (
                  <Popup
                    longitude={mapPoint.coordinates[0]}
                    latitude={mapPoint.coordinates[1]}
                    anchor={popupAnchor}
                    className={styles.mapPopup}
                    closeButton={false}
                    closeOnClick={false}
                    offset={popupOffsets[popupAnchor]}
                  >
                    {mapPoint.link ? (
                      <a
                        className={styles.popupLink}
                        href={mapPoint.link}
                        target={mapPoint.linkTarget || '_parent'}
                        style={{color}}
                      >{mapPoint.label}</a>
                    ) : (<span style={{color}}>{mapPoint.label}</span>)
                    }
                  </Popup>
                )}
                <Marker
                  longitude={mapPoint.coordinates[0]}
                  latitude={mapPoint.coordinates[1]}
                  anchor="bottom"
                  className={styles.mapMarker}
                  offset={[0, 0]}
                  onClick={handleMarkerClick(pointIndex)}
                >
                  <MarkerElement width={40} height={40} fill={color} />
                  {mapPoint.number ? (
                <div className={styles.mapMarkerNumberContainer}>
                  <div className={styles.mapMarkerNumber} style={{color}}>{mapPoint.number}</div>
                </div>
              ) : null}
                </Marker>
              </React.Fragment>
            );
          }
          return (
            <Marker
              longitude={mapPoint.coordinates[0]}
              latitude={mapPoint.coordinates[1]}
              anchor="bottom"
              className={styles.mapMarker}
              offset={[0, 0]}
              key={key}
            >
              <MarkerElement width={40} height={40} fill={color} />
              {mapPoint.number ? (
                <div className={styles.mapMarkerNumberContainer}>
                  <div className={styles.mapMarkerNumber} style={{color}}>{mapPoint.number}</div>
                </div>
              ) : null}
            </Marker>
          );
        });
      } catch(err) {
        return null;
      }
    }
  }

  if (!config) return null;
  if (!isValidCoordinates(config.center)) return null;

  return (
    <Map
      ref={localMapRef}
      initialViewState={{
        longitude: config.center[0],
        latitude: config.center[1],
        zoom: Number.isFinite(config.zoomLevel) ? config.zoomLevel : 10
      }}
      mapStyle="mapbox://styles/lpenaud/cktw16aq40c9y18quz79v7id1"
      onLoad={handleMapLoad}
      onClick={onClick}
      attributionControl={false}
      {...mapOptions}
    >
      {getMapPoints()}
      {getPath()}
    </Map>
  )
});
